import React from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import {
  ListItemText,
  ListItemIcon,
  ListItem,
  ListItemButton,
  List,
  Menu,
  IconButton,
  Checkbox,
  ListSubheader
} from '@mui/material';
import Icon from 'view/Icon'
import Button from '../UI/Button';
import { Border, Main } from 'styles/themeColors';
import { Settings as SettingsIcon } from '@mui/icons-material';

const StyledList = muiStyled(List)<any>(({ $width }) => ({
  width: $width,
  maxWidth: 360,
  padding: '12px !important',
}));

const StyledListV2 = muiStyled(List)<any>({
  width: '400px',
  padding: '0px !important',
  maxHeight: 'auto !important' as any,
});

const StyledListHeader = muiStyled(ListSubheader)<any>({
  fontWeight: 'bold !important',
  position: 'static !important' as any,
  textAlign: 'center',
});

const ListItemHeader = styled.div<any>`
  font-size: 17px !important;
  padding: ${({ $index }) => $index > 0 ? '7px 0px 0px 14px' : '3px 14px'};
  border-top: ${({ $index }) => $index > 0 ? `1px solid ${Border.light}` : '0px'};
  margin-top: ${({ $index }) => $index > 0 ? '5px' : '0px'};
`;

const StyledListItem = muiStyled(ListItemText)<any>({
  '& span': {
    fontSize: '16px !important',
  },
});

interface Props {
  columns: any[];
  onUpdateColumns: (columns: any[]) => void;
  initialColumns: any[];
  showButton?: boolean;
  showIcon?: boolean;
  variant?: 'VARIANT' | 'SERVICES' | null;
  color?: string;
  margin?: string;
};

const MatTableColumnsButton = ({
  columns,
  onUpdateColumns,
  initialColumns,
  showButton = false,
  showIcon = false,
  variant,
  color = Main.brandDark,
  margin = "25px 10px 0 0"
}: Props) => {
  const [myColumns, setMyColumns] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChecked = (title: string) => {
    const updatedColumns = myColumns.map(myColumn => {
      if (myColumn?.title === title) {
        return { ...myColumn, hidden: !myColumn.hidden };
      }

      return { ...myColumn };
    });

    setMyColumns(updatedColumns);
    onUpdateColumns(updatedColumns);
  };

  useEffect(() => {
    if (['VARIANT', 'SERVICES'].includes(variant!)) {
      setMyColumns(columns);
    } else {
      const mapColumnsByInitialOrder = initialColumns.map((initialColumn: any) => columns.find(column => column.field === initialColumn.field));

      setMyColumns(mapColumnsByInitialOrder);
    }
  }, [columns, initialColumns, variant])

  const getListComponent = () => {
    switch (variant) {
      case 'VARIANT':
        return <StyledListV2>
          {
            myColumns.map((myColumn, index) => {
              if (!myColumn || myColumn.isRemoved) return null;
              if (myColumn.isHeading) {
                return <ListItemHeader key={myColumn.title} $index={index}>{myColumn.title}</ListItemHeader>
              } else {
                return (
                  <ListItem
                    key={myColumn.title}
                    disablePadding>
                    <ListItemButton
                      role={undefined}
                      dense
                      onClick={() => onChecked(myColumn.title)}
                      style={{ padding: '5px', paddingLeft: '14px', paddingRight: '14px' }}
                      disabled={myColumn.removable === false || myColumn.isHeading}>
                      <ListItemText primary={myColumn.title} />
                      {
                        myColumn.isHeading === true ? null :
                          <Checkbox
                            edge="start"
                            checked={!(myColumn.hidden)}
                            tabIndex={-1}
                            disableRipple
                            style={{ padding: 0 }}
                          />
                      }
                    </ListItemButton>
                  </ListItem>
                )
              }
            })
          }
        </StyledListV2>

      case 'SERVICES':
        return <StyledListV2>
          {
            myColumns.map((myColumn, index) => {
              if (!myColumn || myColumn.isRemoved) return null;
              if (myColumn.isHeading) {
                return <ListItemHeader $index={index}>{myColumn.title}</ListItemHeader>
              } else {
                return (
                  <ListItem
                    key={myColumn.title}
                    disablePadding>
                    <ListItemButton
                      role={undefined}
                      dense
                      onClick={() => onChecked(myColumn.title)}
                      style={{ padding: '5px', paddingLeft: '14px', paddingRight: '14px', fontSize: '16px' }}
                      disabled={myColumn.removable === false || myColumn.isHeading}>
                      <StyledListItem style={{ fontSize: '16px !important' }} primary={myColumn.title} />
                      {
                        myColumn.isHeading === true ? null :
                          <Checkbox
                            edge="start"
                            checked={!(myColumn.hidden)}
                            tabIndex={-1}
                            disableRipple
                            style={{ padding: 0 }}
                          />
                      }
                    </ListItemButton>
                  </ListItem>
                )
              }
            })
          }
        </StyledListV2>

      default:
        return <StyledList>
          <StyledListHeader>VIEW COLUMNS</StyledListHeader>
          {
            myColumns.map(myColumn => {
              if (!myColumn) return null;
              return (
                <ListItem
                  key={myColumn.title}
                  disablePadding>
                  <ListItemButton
                    role={undefined}
                    dense
                    onClick={() => onChecked(myColumn.title)}
                    disabled={myColumn.removable === false}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={!(myColumn.hidden)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={myColumn.title} />
                  </ListItemButton>
                </ListItem>
              )
            })
          }
        </StyledList>
    }
  }

  return (
    <>
      {!showButton && <IconButton size="medium" onClick={handleOpen}>
        <Icon name='Columns' />
      </IconButton>}
      {showButton && <Button onClick={(event) => {
        setAnchorEl(event.currentTarget);
      }} margin={margin} bgColor={color} borderColor={color}>
        {
          showIcon && <SettingsIcon style={{ marginRight: '5px' }} />
        }
        Column settings
      </Button>}
      <Menu
        id="material-table-columns-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        className='column-settings-container'
      >
        {getListComponent()}
      </Menu>
    </>
  );
};

export default React.memo(MatTableColumnsButton);
